<script setup lang="ts">
import { Button } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { dateTimeFormattedShort } from '@/common/dateUtils'
import Duration from '@/features/eventDetails/components/Duration.vue'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useModalStore } from '@/features/modal/useModalStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const { deleteEvent } = useEventDetailsStore()
const { store, products, startDate, endDate, eventDuration } = storeToRefs(useEventDetailsStore())
const { trackEvent } = useTelemetry()
const { closeDeleteEventConfirmationModal } = useModalStore()
const { isDeleteEventConfirmationModalVisible } = storeToRefs(useModalStore())
const isRemoving = ref(false)

const cancelClicked = () => {
	trackEvent('user', 'Cancelled deleting an event.', { store: store.value?.displayName })

	closeDeleteEventConfirmationModal()
}

const removeClicked = async () => {
	trackEvent('user', 'Deleted an event.', { store: store.value?.displayName })

	isRemoving.value = true

	await deleteEvent()

	isRemoving.value = false

	closeDeleteEventConfirmationModal()
}

const firstProductName = computed(() => {
	return products.value[0]?.humanName
})

const allProductNames = computed(() => {
	return products.value.map(({ humanName }) => humanName).join(', ')
})

const isDeletingForMoreThanOneProduct = computed(() => products.value.length > 1)

const eventRange = computed(() => {
	return (
		startDate.value?.isValid &&
		endDate.value?.isValid &&
		`${dateTimeFormattedShort(startDate.value.toUTC(), true)} - ${dateTimeFormattedShort(endDate.value.toUTC(), true)}`
	)
})
</script>

<template>
	<div>
		<div v-if="isDeleteEventConfirmationModalVisible" class="absolute left-0 top-0 z-40 flex h-full w-full items-center justify-center bg-black/50">
			<div class="flex w-[520px] flex-col rounded-md bg-level-01 p-7">
				<h2 class="mb-6 text-2xl font-bold">REMOVE YOUR CUSTOM EVENT?</h2>
				<p v-if="isDeletingForMoreThanOneProduct" class="modal-text mb-2 text-sm">
					You're about to remove an event for products:
					<strong>{{ allProductNames }}</strong>
				</p>
				<p v-else class="modal-text mb-2 text-sm">
					You're about to remove
					<strong>{{ firstProductName }}</strong> event from your timeline
				</p>
				<p class="mb-6 flex items-center text-sm">
					<mdicon name="calendar-today" />
					&nbsp;{{ eventRange }} (<Duration :duration="eventDuration" short />)
				</p>
				<div class="flex items-center">
					<p class="mb-5 rounded-md bg-level-03 p-4 text-sm">
						Please remember that removing the event from the IndieBI’s timeline
						<b>doesn’t remove it from the portal’s backend</b>, so if it’s already been submitted you need to manually remove it.
					</p>
				</div>
				<div class="mt-4 flex justify-around">
					<Button variant="secondary" :disabled="isRemoving" @click="cancelClicked">Cancel</Button>
					<Button icon="delete" :loading="isRemoving" @click="removeClicked">Delete</Button>
				</div>
			</div>
		</div>
	</div>
</template>
